import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Film, Search, Trash2, Upload } from "lucide-react";
import { api, apiError, formatDate, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import {
  AdminBanner,
  AdminButton,
  AdminCard,
  AdminPageHead,
  AdminSpinner,
  EmptyState,
  FilterTabs,
  adminInputClass,
} from "@/components/admin/AdminUi";

const TABS = [
  { value: "all", label: "All" },
  { value: "images", label: "Pictures" },
  { value: "videos", label: "Videos" },
  { value: "documents", label: "Documents" },
];

function prettySize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MediaLibrary() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [banner, setBanner] = useState({ status: "idle", message: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api
      .get("/admin/media", { params: { kind, q: q || undefined } })
      .then((r) => setItems(r.data || []))
      .catch((e) => {
        setItems([]);
        setBanner({ status: "error", message: apiError(e) });
      });
  }, [kind, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setBanner({ status: "idle", message: "" });
    const body = new FormData();
    body.append("file", file);

    try {
      await api.post("/admin/media", body, { headers: { "Content-Type": "multipart/form-data" } });
      setBanner({ status: "ok", message: `"${file.name}" was uploaded. You can now choose it directly while editing a page.` });
      load();
    } catch (err) {
      setBanner({ status: "error", message: apiError(err) });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.original_filename}"?`)) return;
    try {
      await api.delete(`/admin/media/${item.id}`);
      setBanner({ status: "ok", message: "File deleted." });
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  return (
    <div data-testid="cms-media">
      <Seo title="Media Library | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead title="Pictures & Media" subtitle="Upload and manage the files used on the website.">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <label htmlFor="media-search" className="sr-only">Search media</label>
          <input
            id="media-search"
            data-testid="cms-media-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search pictures…"
            className={`${adminInputClass} w-56 pl-9`}
          />
        </div>

        {can("media.write") && (
          <>
            <input
              id="media-upload"
              ref={fileRef}
              data-testid="cms-media-file-input"
              type="file"
              onChange={upload}
              accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,application/pdf,video/mp4"
              className="hidden"
            />
            <AdminButton
              variant="emerald"
              testId="cms-media-upload-button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload Picture / File"}
            </AdminButton>
          </>
        )}
      </AdminPageHead>

      <AdminBanner
        status={banner.status}
        message={banner.message}
        onDismiss={() => setBanner({ status: "idle", message: "" })}
      />

      <div className="mb-5 rounded-2xl border border-emerald-100 bg-emerald-50/50 px-5 py-4">
        <p className="font-bold text-bka-navy">You do not need to copy any links.</p>
        <p className="mt-1 text-sm text-slate-600">
          To put a picture on a page: open <strong>Pages</strong> → choose the page → click
          <strong> Add Picture</strong> or <strong>Choose Picture</strong> → click the picture you want.
        </p>
      </div>

      <FilterTabs tabs={TABS} value={kind} onChange={setKind} testIdPrefix="cms-media-filter" />

      {items === null ? (
        <AdminCard><AdminSpinner /></AdminCard>
      ) : items.length === 0 ? (
        <AdminCard>
          <EmptyState message="No files yet. Click Upload Picture / File to add your first one." testId="cms-media-empty" />
        </AdminCard>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((m) => {
            const isImage = (m.mime_type || "").startsWith("image/");
            const isVideo = (m.mime_type || "").startsWith("video/");
            const url = mediaUrl(`/api/media/${m.id}/file`);

            return (
              <AdminCard key={m.id} testId={`cms-media-item-${m.id}`} className="overflow-hidden">
                <div className="flex h-40 items-center justify-center bg-slate-100">
                  {isImage ? (
                    <img src={url} alt={m.alt_text || m.original_filename} className="h-full w-full object-cover" loading="lazy" />
                  ) : isVideo ? (
                    <Film className="h-9 w-9 text-slate-400" />
                  ) : (
                    <FileText className="h-9 w-9 text-slate-400" />
                  )}
                </div>

                <div className="p-4">
                  <p className="truncate text-sm font-bold text-bka-navy" title={m.original_filename}>
                    {m.original_filename}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {prettySize(m.size_bytes)} · {formatDate(m.created_at)}
                  </p>

                  {can("media.write") && (
                    <button
                      type="button"
                      onClick={() => remove(m)}
                      aria-label={`Delete ${m.original_filename}`}
                      data-testid={`cms-media-delete-${m.id}`}
                      className="mt-3 flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-500 hover:border-red-300 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  )}
                </div>
              </AdminCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
